import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/lombard-l1";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const lombardL1: CaseStudy = {
  slug: "lombard-l1",
  title: "Ломбард-Л1",
  cardTitle: "Разработка сайта для сети ломбардов «Ломбард-Л1»",
  subtitle: "Сеть ломбардов",
  industry: { label: "Финансовые услуги", slug: "finance" },
  eyebrow: "Корпоративный сайт · Финансовые услуги",
  h1: "Разработка сайта для сети «Ломбард-Л1»",
  lead:
    "Разработали сайт сети ломбардов «Ломбард-Л1»: разделы залога техники и ювелирных изделий, тарифы и акции, офисы и поддержку, а также цифровые сервисы компании.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить сайт финансовой компании" },

  seo: {
    primaryKeyword: "разработка сайта для сети ломбардов",
    secondaryKeywords: [
      "сайт ломбарда разработка",
      "создание сайта для ломбардной сети",
      "личный кабинет ломбарда",
      "сайт финансовой компании",
    ],
    semanticEntities: [
      "сеть ломбардов",
      "залог техники",
      "залог ювелирных изделий",
      "тарифы и акции",
      "офисы и поддержка",
      "личный кабинет",
      "мобильное приложение",
      "реестр ломбардов Банка России",
    ],
    excludedKeywords: [
      "сдать золото",
      "ломбард рядом",
      "займ под золото",
      "цена золота в ломбарде",
    ],
    title: "Сайт сети «Ломбард-Л1» — кейс HAY.agency",
    description:
      "Кейс HAY.agency: сайт сети ломбардов с направлениями залога, тарифами, акциями, офисами, поддержкой и цифровыми сервисами.",
    h1: "Разработка сайта для сети «Ломбард-Л1»",
    slug: "lombard-l1",
    canonicalUrl: `${SITE_URL}/cases/lombard-l1`,
    ogTitle: "Сайт сети ломбардов «Ломбард-Л1»",
    ogDescription:
      "Направления залога, тарифы, акции, офисы, поддержка и цифровые сервисы сети ломбардов.",
    ogImage: `${SITE_URL}${dir}/lombard-l1-og.webp`,
    robotsIndex: true,
    datePublished: "2024-01-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка корпоративных сайтов",
      to: routes.corporate,
      note: "Структура услуг, тарифы и заявки",
    },
    relatedIndustry: {
      label: "Сайты и маркетинг для ломбардов",
      to: routes.lombard,
      note: "Отраслевые решения для ломбардного бизнеса",
    },
    relatedCases: [
      {
        label: "Сайт ломбарда «Семёрочка» с онлайн-оценкой",
        to: "/cases/7-lombard",
        note: "Похожий проект в финансовой отрасли",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Давайте обсудим, как мы можем масштабировать ваш бизнес",
    text: "Оставьте заявку — свяжемся в течение двух часов, чтобы задать вопросы и обсудить задачу.",
    button: "Обсудить сайт финансовой компании",
  },

  client: {
    name: "Ломбард-Л1",
    url: "https://lombard-l1.ru/",
    city: "Россия",
    industry: "Финансовые услуги",
  },
  year: "2024",
  services: [
    "Разработка корпоративного сайта",
    "Разделы залога техники и ювелирных изделий",
    "Тарифы и акции",
    "Офисы и поддержка",
    "Цифровые сервисы",
  ],
  platform: "Собственная разработка",
  tags: [
    { label: "Корпоративный сайт", to: routes.corporate },
    { label: "Разработка", to: routes.sites },
    { label: "Поддержка", to: routes.corporate },
  ],
  facts: [
    { label: "Клиент", value: "Ломбард-Л1" },
    { label: "Отрасль", value: "Финансовые услуги" },
    { label: "Тип проекта", value: "Сайт сети ломбардов" },
    { label: "Год", value: "2024" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Сайт с разделением залога техники и ювелирных изделий, видов займов и акций",
      "Тарифный блок и раздел акций с юридической информацией",
      "Поиск и контакты офисов сети, раздел поддержки",
      "Информация о присутствии компании в реестре ломбардов Банка России",
      "Ссылки на мобильное приложение и способы оплаты в приложении",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "У сети ломбардов два разных клиентских сценария: залог техники и залог ювелирных изделий, у каждого свои условия и тарифы. Сайт должен был развести эти сценарии, не запутав посетителя, и одновременно показать надёжность компании — присутствие в реестре ломбардов Банка России, контакты офисов и поддержку.",
        "Отдельная задача — связать сайт с цифровыми сервисами компании: личным кабинетом, приложением и оплатой, не показывая при этом чувствительные данные пользователей.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: нейтральный и деловой тон финансового сервиса",
    paragraphs: [
      "Интерфейс построен на графитовой палитре без ярких акцентов — сайт выглядит как сервис финансовой компании, а не как витрина ломбарда.",
      "Направления залога вынесены на первый экран как две равнозначные точки входа, чтобы посетитель сразу попадал в нужный сценарий.",
    ],
    bullets: [
      "Нейтральная графитовая палитра",
      "Две точки входа — залог техники и залог ювелирных изделий",
      "Юридическая информация рядом с тарифами и акциями",
    ],
  },

  identity: {
    fonts: [],
    colors: [
      { hex: "#EDEDED", name: "Основной фон" },
      { hex: "#2B2E33", name: "Текст и акцент" },
      { hex: "#C7A15B", name: "Акцент бренда" },
    ],
  },

  cover: img(
    "lombard-l1-case-cover",
    "Главная страница сайта сети ломбардов «Ломбард-Л1»",
    6,
  ),

  gallery: [
    img("lombard-l1-mortgage-types", "Разделы залога техники и ювелирных изделий на сайте Ломбард-Л1", 3),
    img("lombard-l1-tariffs", "Тарифы и акции сети ломбардов Ломбард-Л1", 3),
    img("lombard-l1-offices", "Офисы и поддержка сети Ломбард-Л1", 6),
  ],

  responsive: {
    text: [
      "Мобильная версия повторяет логику десктопа: выбор направления залога, тарифы и переход к офису или приложению на одном экране.",
      "Смысловые блоки на мобильном расположены горизонтальным скроллом, чтобы посетитель мог быстро сравнить условия.",
    ],
    images: [
      mobile("lombard-l1-mobile-home", "Мобильная главная сайта Ломбард-Л1"),
      mobile("lombard-l1-mobile-services", "Мобильные разделы залога и тарифов"),
    ],
  },

  columnBlocks: [
    {
      id: "collateral",
      title: "Сценарии по типу залога",
      paragraphs: [
        "Сайт разводит два направления — залог техники и залог ювелирных изделий — с собственными условиями, тарифами и акциями для каждого.",
      ],
      columns: [
        { title: "Залог техники", items: ["Условия займа", "Виды принимаемой техники", "Тарифы"] },
        { title: "Залог ювелирных изделий", items: ["Условия займа", "Оценка изделия", "Тарифы"] },
      ],
      caption: "Каждое направление ведёт к собственному тарифу и акции.",
    },
    {
      id: "trust",
      title: "Офисы, поддержка и цифровые сервисы",
      paragraphs: [
        "Раздел офисов помогает найти отделение и связаться с поддержкой, а цифровые сервисы — личный кабинет, приложение и оплата — показаны только в разрешённом к публикации объёме.",
      ],
      bullets: [
        "Поиск и контакты офисов сети",
        "Раздел обращений и поддержки",
        "Присутствие компании в реестре ломбардов Банка России",
        "Ссылки на приложение и оплату в приложении",
      ],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка корпоративного сайта", to: routes.corporate },
      { label: "Поддержка и развитие", to: routes.corporate },
    ],
  },
};
